import { Page } from "@/components/page-base";
import { ContainerData } from "../styles";
import { Container, FormPixType, HeaderCredits } from "./styles";

import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import ShareIcon from "@mui/icons-material/Share";
import Form from "@/components/Form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import { EmbaixadorService } from "../../services";
import { toast } from "react-toastify";
import { useAuthContext } from "@/contexts";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import permissionsStore from "../../../../store/permissions";
import { Permissions } from "@/modules/users/models";
export const BecomeMember: React.FC = () => {
  const { user, setIsAmbassadorContext } = useAuthContext();
  const { setPermissions, permissions } = permissionsStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const schemaPixKeyForm = z
    .object({
      pixKeytype: z.enum(["CPF", "CNPJ", "EMAIL", "PHONE"]),
      pixKeyvalue: z.string().nonempty("A chave é obrigatório"),
    })
    .transform(({ pixKeytype, pixKeyvalue }) => {
      if (["CPF", "CNPJ", "PHONE"].includes(pixKeytype)) {
        return { pixKeytype, pixKeyvalue: pixKeyvalue.replace(/\D/g, "") };
      }
      return { pixKeytype, pixKeyvalue };
    })
    .superRefine((data, ctx) => {
      if (data.pixKeytype === "PHONE" && data.pixKeyvalue.length !== 11) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Telefone precisa ter 11 dígitos",
          path: ["pixKeyvalue"],
        });
      }
      if (data.pixKeytype === "CPF" && data.pixKeyvalue.length !== 11) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "CPF precisa ter 11 dígitos",
          path: ["pixKeyvalue"],
        });
      }
      if (data.pixKeytype === "CNPJ" && data.pixKeyvalue.length !== 14) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "CNPJ precisa ter 14 dígitos",
          path: ["pixKeyvalue"],
        });
      }
    });

  const PixKeyForm = useForm<PixKeyFormProps>({
    resolver: zodResolver(schemaPixKeyForm),
  });

  type PixKeyFormProps = z.infer<typeof schemaPixKeyForm>;

  const handleSubmit = async (data: PixKeyFormProps) => {
    try {
      setLoading(true);
      setIsAmbassadorContext(true);

      const embaixadorResult = await EmbaixadorService.setEmbaixador();

      if (embaixadorResult.success && user) {
        const pixKeyResult = await EmbaixadorService.sendPixKey({
          pixKeytype: data.pixKeytype,
          key: data.pixKeyvalue,
        });

        if (pixKeyResult) {
          const newPermissions = [
            ...permissions.filter((p) => p !== Permissions.SEJAMBASSADOR),
            Permissions.AMBASSADOR,
          ];

          setPermissions(newPermissions);

          // Força uma atualização do estado
          await new Promise<void>((resolve) => {
            // Verifica se o estado foi atualizado
            const checkState = () => {
              const currentPermissions =
                permissionsStore.getState().permissions;
              if (currentPermissions.includes(Permissions.AMBASSADOR)) {
                resolve();
              } else {
                setTimeout(checkState, 50);
              }
            };
            checkState();
          });

          toast.success("Parabéns, você se tornou um embaixador PuxaCapivara!");
          navigate("/ambassador");
        }
      } else {
        toast.error(embaixadorResult.message);
      }
    } catch (error) {
      console.error("Erro ao processar cadastro de embaixador:", error);
      toast.error("Ocorreu um erro ao processar seu cadastro como embaixador.");
    } finally {
      setLoading(false);
    }
  };

  const optionsSelect = [
    { value: "CPF", label: "CPF" },
    { value: "CNPJ", label: "CNPJ" },
    { value: "EMAIL", label: "EMAIL" },
    { value: "PHONE", label: "TELEFONE" },
  ];

  return (
    <Page.Root>
      <Page.Header title="Embaixador Puxa" key="embaixador" />
      <ContainerData>
        <Container>
          <HeaderCredits className="laptop:!mb-0 mobile:mt-10">
            <h1 className={cn("mobile:!text-[28px]")}>
              Puxa Capivara, Indique e Ganhe!
            </h1>
            <span className="mobile:!text-[14px] mt-2">
              Receba 25% das suas indicações de forma recorrente
            </span>
          </HeaderCredits>
          <Dialog>
            <DialogTrigger className="border-2 p-10 mt-4 leading-4  animate-zoom flex items-center justify-center border-borderCreditsDiv text-white bg-black rounded-[18px] h-14">
              <ShareIcon sx={{ width: 30, height: 30, marginRight: "5px" }} />
              <span className="font-bold text-nowrap">Quero Indicar</span>
            </DialogTrigger>
            <DialogContent className="border-0 overflow-hidden bg-pcPrimaryColor">
              <DialogHeader className="p-6 bg-white">
                <DialogTitle>Indique e Ganhe</DialogTitle>
                <DialogDescription className="text-xs">
                  Cadastre sua chave para realizar os seus saques
                </DialogDescription>
              </DialogHeader>
              <FormProvider {...PixKeyForm}>
                <FormPixType onSubmit={PixKeyForm.handleSubmit(handleSubmit)}>
                  <Form.Field height={54}>
                    <Form.Label className="text-white">
                      Selecione e digite a sua chave
                    </Form.Label>
                    <Form.Select
                      name="pixKeytype"
                      className="bg-white h-10 !p-2 !text-base"
                      options={optionsSelect}
                    />
                    <Form.ErrorMessage field="pixKeytype" />
                  </Form.Field>

                  <Form.Field height={54}>
                    <Form.Input
                      className="h-10 !p-2 !text-base"
                      name="pixKeyvalue"
                      placeholder="Digite sua chave pix"
                      maskType="text"
                    />
                    <Form.ErrorMessage field="pixKeyvalue" />
                  </Form.Field>

                  <Form.Button
                    disabled={loading}
                    type="submit"
                    className={cn("hover:brightness-90 !py-2", {
                      "animate-pulse": loading,
                    })}
                  >
                    {loading ? "Cadastrando..." : "Cadastrar"}
                  </Form.Button>
                </FormPixType>
              </FormProvider>
            </DialogContent>
          </Dialog>
        </Container>
      </ContainerData>
    </Page.Root>
  );
};
